"use client";

import { cn } from "@ui/lib/utils";
import { useCallback, useEffect, useImperativeHandle, useRef } from "react";

export interface MediaElement {
  addEventListener(eventName: "loadedmetadata" | "pause" | "play", fn: () => void): void;
  currentTime: number;
  duration: number;
  paused: boolean;
  removeEventListener(eventName: "loadedmetadata" | "pause" | "play", fn: () => void): void;
  seek?(time: number): void;
}

export const createHowlWrapper = (howl: Howl): MediaElement => {
  return {
    addEventListener: (eventName: "pause" | "play", fn: () => void) => {
      howl.on(eventName, fn);
    },
    removeEventListener: (eventName: "pause" | "play", fn: () => void) => {
      howl.off(eventName, fn);
    },
    get currentTime() {
      return howl.seek();
    },
    get duration() {
      return howl.duration();
    },
    get paused() {
      return !howl.playing();
    },
  };
};

export const createAudioWrapper = (audio: HTMLAudioElement): MediaElement => {
  return {
    addEventListener: (eventName: "pause" | "play", fn: () => void) => {
      audio.addEventListener(eventName, fn);
    },
    removeEventListener: (eventName: "pause" | "play", fn: () => void) => {
      audio.removeEventListener(eventName, fn);
    },
    get currentTime() {
      return audio.currentTime;
    },
    get duration() {
      return audio.duration;
    },
    get paused() {
      return audio.paused;
    },
    seek: (time: number) => {
      audio.currentTime = time;
    },
  };
};

export interface MediaProgressBarRef {
  seek: (time: number) => void;
}

function MediaProgressBar({
  mediaElement,
  className,
  ref,
  inverse = false,
}: {
  className?: string;
  inverse?: boolean;
  mediaElement: MediaElement | null;
  ref?: React.Ref<MediaProgressBarRef | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const animationStartTime = useRef(0);
  const videoStartTime = useRef(0);
  const mediaDuration = useRef(0);
  const animationId = useRef(0);
  const isPlaying = useRef(false);

  const updateProgressBar = useCallback(function updateProgressBar() {
    if (!containerRef.current || !progressBarRef.current || !isPlaying.current) return;

    const elapsedTime = (Date.now() - animationStartTime.current) / 1000;
    const currentTime = elapsedTime + videoStartTime.current;

    if (currentTime <= mediaDuration.current) {
      const progressBarWidth = containerRef.current?.clientWidth * (currentTime / mediaDuration.current);
      progressBarRef.current.style.transform = `translateX(${progressBarWidth}px)`;
      animationId.current = requestAnimationFrame(updateProgressBar);
    } else {
      progressBarRef.current.style.transform = `translateX(0px)`;
    }
  }, []);

  const onPlay = useCallback(() => {
    if (!mediaElement) return;
    const { currentTime, duration } = mediaElement;

    // Wait for duration to be available
    if (isNaN(duration) || duration <= 0) {
      // Set up a one-time listener for when metadata loads
      const handleLoadedMetadata = () => {
        onPlay();
        mediaElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
      mediaElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      return;
    }

    mediaDuration.current = duration;
    animationStartTime.current = Date.now();
    videoStartTime.current = currentTime;
    isPlaying.current = true;

    updateProgressBar();
  }, [mediaElement, updateProgressBar]);

  const onPause = useCallback(() => {
    isPlaying.current = false;
    cancelAnimationFrame(animationId.current);

    // Update the video start time to current position when paused
    if (mediaElement) {
      const elapsedTime = (Date.now() - animationStartTime.current) / 1000;
      videoStartTime.current = elapsedTime + videoStartTime.current;
      animationStartTime.current = Date.now();
    }
  }, [mediaElement]);

  useEffect(() => {
    if (!mediaElement) return;
    cancelAnimationFrame(animationId.current);
    animationStartTime.current = 0;
    videoStartTime.current = 0;
    mediaDuration.current = 0;
    isPlaying.current = false;
    updateProgressBar();

    mediaElement.addEventListener("play", onPlay);
    mediaElement.addEventListener("pause", onPause);

    // Check if audio is already playing when component mounts/mediaElement changes
    // This handles the auto-play case
    requestAnimationFrame(() => {
      if (mediaElement && !mediaElement.paused && mediaElement.duration > 0) {
        onPlay();
      }
    });

    return () => {
      mediaElement.removeEventListener("play", onPlay);
      mediaElement.removeEventListener("pause", onPause);
      cancelAnimationFrame(animationId.current);
    };
  }, [mediaElement, onPlay, onPause, updateProgressBar]);

  const seek = useCallback(
    (time: number) => {
      if (!containerRef.current || !progressBarRef.current || !mediaElement) return;

      if (typeof mediaElement.seek === "function") {
        mediaElement.seek(time);
      } else {
        mediaElement.currentTime = time;
      }

      const progressBarWidth = containerRef.current.clientWidth * (time / mediaDuration.current);
      progressBarRef.current.style.transform = `translateX(${progressBarWidth}px)`;

      animationStartTime.current = Date.now();
      videoStartTime.current = time;
    },
    [mediaElement],
  );

  useImperativeHandle(ref, () => ({
    seek,
  }));

  const handleProgressBarClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current || !mediaElement) return;

      const rect = containerRef.current.getBoundingClientRect();
      const clickPosition = event.clientX - rect.left;
      const percentageClicked = clickPosition / rect.width;
      const newTime = percentageClicked * mediaDuration.current;

      seek(newTime);
    },
    [mediaElement, seek],
  );

  return (
    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    <div
      className={cn(
        "relative mx-16 h-2 cursor-pointer overflow-hidden rounded-full",
        inverse ? "bg-[rgba(255,255,255,0.2)]" : "bg-dark",
        className,
      )}
      onClick={handleProgressBarClick}
      ref={containerRef}
    >
      <div
        className={`transition-width absolute -left-full h-full w-full rounded-full ${inverse ? "bg-[rgba(255,255,255,1)]" : "bg-inverse"}`}
        ref={progressBarRef}
      />
    </div>
    /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  );
}

export default MediaProgressBar;
